import { useState, useEffect } from 'react'
import { supabase } from './lib/supabase'
import { api } from './lib/api'
import Login from './pages/Login'
import Onboarding from './pages/Onboarding'
import Dashboard from './pages/Dashboard'
import { BillingSuccess, BillingCancel } from './pages/BillingPages'
import ResetPassword from './pages/ResetPassword'

export default function App() {
  const [user, setUser] = useState(null)
  const [business, setBusiness] = useState(null)
  const [loading, setLoading] = useState(true)
  const [businessLoading, setBusinessLoading] = useState(false)
  const [accessBlocked, setAccessBlocked] = useState(false)

  const pathname = window.location.pathname
  if (pathname === '/billing/success') return <BillingSuccess />
  if (pathname === '/billing/cancel') return <BillingCancel />
  if (pathname === '/reset-password') return <ResetPassword />

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null)
      if (session?.user) {
        setBusinessLoading(true)
        loadBusiness(session.user.id)
      } else {
        setLoading(false)
      }
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null)
      if (session?.user) {
        if (event === 'PASSWORD_RECOVERY') {
          window.location.href = '/reset-password'
          return
        }
        setBusinessLoading(true)
        loadBusiness(session.user.id)
      } else {
        setBusiness(null)
        setLoading(false)
        setBusinessLoading(false)
        setAccessBlocked(false)
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  async function loadBusiness(userId) {
    try {
      const biz = await api.getBusiness(userId)
      setBusiness(biz)
      const blocked = isAccessBlocked(biz.subscription_status)
      setAccessBlocked(blocked)
    } catch {
      setBusiness(null)
    } finally {
      setLoading(false)
      setBusinessLoading(false)
    }
  }

  function isAccessBlocked(status) {
    if (!status || status === 'trial' || status === 'trialing' || status === 'active') {
      return false
    }
    if (status === 'canceled' || status === 'past_due') {
      return true
    }
    return false
  }

  function handleBusinessCreated(biz) {
    setBusiness(biz)
    setAccessBlocked(false)
  }

  function handleBusinessUpdated(biz) {
    setBusiness(biz)
    setAccessBlocked(isAccessBlocked(biz.subscription_status))
  }

  if (loading || businessLoading) {
    return (
      <div style={loadingStyles}>
        <div style={loadingBoxStyles}>
          <span style={loadingTextStyles}>JC</span>
        </div>
      </div>
    )
  }

  if (!user) return <Login />
  if (!business) return <Onboarding user={user} onComplete={handleBusinessCreated} />

  if (accessBlocked) {
    return (
      <div style={paywallStyles.container}>
        <div style={paywallStyles.card}>
          <div style={paywallStyles.icon}>JC</div>
          <h1 style={paywallStyles.title}>Your subscription has ended</h1>
          <p style={paywallStyles.sub}>
            Reactivate your JobConfirmed subscription to continue sending appointment reminders.
          </p>
          <button
            style={paywallStyles.btn}
            onClick={async () => {
              try {
                const { url } = await api.createCheckoutSession({
                  user_id: user.id,
                  business_id: business.id,
                  email: user.email,
                })
                window.location.href = url
              } catch (err) {
                alert('Something went wrong. Please try again.')
              }
            }}
          >
            Reactivate — $39.99/mo
          </button>
          <button
            style={paywallStyles.signOut}
            onClick={() => supabase.auth.signOut()}
          >
            Sign out
          </button>
        </div>
      </div>
    )
  }

  return <Dashboard user={user} business={business} onBusinessUpdate={handleBusinessUpdated} />
}

const loadingStyles = {
  minHeight: '100vh',
  background: '#0a0a0f',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

// Full-width grey banner, vertically centered on the page, holding the
// loading mark. Matches the header's background (#13131a) so it reads as
// an intentional panel rather than plain text floating on the dark page.
const loadingBoxStyles = {
  width: '100%',
  background: '#13131a',
  padding: '60px 0',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const loadingTextStyles = {
  fontSize: 32,
  fontWeight: 700,
  color: '#fff',
  letterSpacing: 2,
  fontFamily: "'DM Sans', sans-serif",
}

const paywallStyles = {
  container: {
    minHeight: '100vh',
    background: '#0a0a0f',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: "'DM Sans', sans-serif",
    padding: 20,
  },
  card: {
    background: '#13131a',
    border: '1px solid #1e1e2e',
    borderRadius: 16,
    padding: '48px 40px',
    maxWidth: 420,
    width: '100%',
    textAlign: 'center',
  },
  icon: { fontSize: 40, marginBottom: 16 },
  title: { color: '#fff', fontSize: 22, fontWeight: 700, margin: '0 0 12px' },
  sub: { color: '#6b6b80', fontSize: 14, lineHeight: 1.6, margin: '0 0 32px' },
  btn: {
    width: '100%', background: '#4f46e5', color: '#fff',
    border: 'none', borderRadius: 8, padding: '13px 0',
    fontSize: 15, fontWeight: 600, cursor: 'pointer', marginBottom: 12,
  },
  signOut: {
    width: '100%', background: 'none', color: '#6b6b80',
    border: 'none', fontSize: 13, cursor: 'pointer', padding: '8px 0',
  },
}
